import { keysOf, boot } from '@dazn/bootstrap-lib';

import { getPath, getQuery, getHash } from '../utils/location';
import { ensureLeadingSlash } from '../utils/slash';

const LANG_SEGMENT_RX = /^[a-z]{2}-(?:[a-z]{2}|global)$/i;

const isLangSegment = (pathSegment: string) =>
    LANG_SEGMENT_RX.test(pathSegment);

const getPathSegments = (path: string) => path.split('/').filter(Boolean);

const buildPath = (pathname: string, location: Location) =>
    ensureLeadingSlash(
        [
            pathname,
            /\?/.test(pathname) ? '' : getQuery(location),
            /#/.test(pathname) ? '' : getHash(location),
        ].join(''),
    );

const getLangSegment = (country: string, lang = 'en') =>
    [lang.toLowerCase(), country.toUpperCase()].join('-');

const getPathnameWithLang = (
    pathname: string,
    langSegment: string,
    rewriteUrlLocale = true,
) => {
    const urlSegments = getPathSegments(pathname);
    const hasLanguageSegment = urlSegments.some(isLangSegment);

    let urlPath = pathname;
    if (hasLanguageSegment && rewriteUrlLocale) {
        urlPath = urlSegments
            .map(s => (isLangSegment(s) ? langSegment : s))
            .join('/');
    }
    if (!hasLanguageSegment) {
        urlPath = [langSegment].concat(urlSegments).join('/');
    }
    return urlPath;
};

const getChaptersMatchingPath = (
    chapterMap: boot.config.ChapterMap<string[]>,
    pathname: string,
) => {
    const nonLangSegments = getPathSegments(pathname).filter(
        seg => !isLangSegment(seg),
    );
    const firstNonLangSegment = nonLangSegments[0];

    return keysOf(chapterMap).filter(chapterName =>
        chapterMap[chapterName]!.some(
            chapterPage =>
                firstNonLangSegment ===
                chapterPage.split('/').filter(Boolean)[0],
        ),
    );
};

const shouldRewriteLocale = (
    currentPath: string,
    defaultValue: boolean,
    overrideMap?: Record<string, boolean>,
) => {
    if (!overrideMap) {
        return defaultValue;
    }

    for (const rxStr of Object.keys(overrideMap)) {
        const re = new RegExp(rxStr, 'i');
        if (re.test(currentPath)) {
            return overrideMap[rxStr];
        }
    }

    return defaultValue;
};

export type ComputeChapterURLParams = {
    location: Location;
    chaptersMap: boot.config.ChapterMap<string[]>;
    country: string;
    language: string;
    chapterHomepage: string;
    withLanguageInUrl: boolean;
    chapterFromUrl: boot.config.ChapterName;
    chapterName: boot.config.ChapterName;
    chapterPath: string;
    rewriteUrlLocale: boolean;
    rewriteUrlLocaleOverrideMap: Record<string, boolean> | undefined;
};
export const computeChapterURL = ({
    location,
    chaptersMap,
    country,
    language,
    chapterHomepage,
    withLanguageInUrl,
    chapterFromUrl,
    chapterName,
    chapterPath,
    rewriteUrlLocale,
    rewriteUrlLocaleOverrideMap,
}: ComputeChapterURLParams) => {
    const currentUrlIsForSameChapter = chapterFromUrl === chapterName;

    const doRewrite = shouldRewriteLocale(
        location.pathname,
        rewriteUrlLocale,
        rewriteUrlLocaleOverrideMap,
    );

    /*
     * We're being asked to load a new chapter just using a string
     * In this case we need to build a url from scratch
     */
    if (!currentUrlIsForSameChapter && !chapterPath) {
        const newPath = getPathnameWithLang(
            chapterHomepage,
            getLangSegment(country, language),
            doRewrite,
        );
        return buildPath(
            withLanguageInUrl ? newPath : chapterHomepage,
            location,
        );
    }

    /*
     * Here we are either being asked to load the chapter we're already on
     * Or we are following a deeplink
     * Or a path was given when setting chapterHistory.currentChapter
     * So we just check that the url has the correct language / region
     */
    if (
        currentUrlIsForSameChapter ||
        location.pathname !== '/' ||
        Boolean(chapterPath)
    ) {
        const pathname = chapterPath || getPath(location);
        /* Should use the chapter's homePage if:
            - requesting /
            OR
            - requesting any URL where:
                - the chapter in URL doesn't exists in chapters map
        */
        const shouldUseHomepage =
            pathname === '/' ||
            (location.pathname !== '/' &&
                !getChaptersMatchingPath(chaptersMap, pathname).includes(
                    chapterFromUrl,
                ));
        const chapterPathName = shouldUseHomepage ? chapterHomepage : pathname;
        const newPath = getPathnameWithLang(
            chapterPathName,
            getLangSegment(country, language),
            doRewrite,
        );
        return buildPath(
            withLanguageInUrl ? newPath : chapterPathName,
            location,
        );
    }

    /*
     * The default - this means the url is "/"
     * so we will load the homepage of whatever chapter we've been asked to load
     */
    const newPath = getPathnameWithLang(
        chapterPath,
        getLangSegment(country, language),
        doRewrite,
    );
    return buildPath(withLanguageInUrl ? newPath : chapterPath, location);
};
